import { useCallback, useState } from 'react';
import { useMatch } from 'react-router';

/**
 * The project whose menu the sidebar shows. Inside a project route it is that project; elsewhere (workspace,
 * administration) it is the last project this user opened in this browser, so the project menu does not
 * disappear when leaving a project page. Remembered per user in localStorage; storage failures (private mode,
 * blocked storage) only mean the menu is not restored.
 */
const storageKey = (userId: string) => `lj.lastProject.${userId}`;

function read(userId: string): string | null {
  try {
    return localStorage.getItem(storageKey(userId));
  } catch {
    return null;
  }
}

function write(userId: string, key: string | null) {
  try {
    if (key) localStorage.setItem(storageKey(userId), key);
    else localStorage.removeItem(storageKey(userId));
  } catch {
    /* not persisted; the menu still follows the current route */
  }
}

export function useActiveProject(userId: string) {
  const match = useMatch('/projects/:key/*');
  const routeKey = match?.params.key && match.params.key !== 'new' ? match.params.key.toUpperCase() : null;
  const [remembered, setRemembered] = useState(() => read(userId));

  /** Remembers a project once it has loaded, so a mistyped URL never replaces the remembered one. */
  const remember = useCallback(
    (key: string) => {
      write(userId, key);
      setRemembered(key);
    },
    [userId],
  );

  /** Drops the remembered project (no longer accessible, or the user hides it). */
  const forget = useCallback(() => {
    write(userId, null);
    setRemembered(null);
  }, [userId]);

  return {
    key: routeKey ?? remembered,
    /** True when the menu shows a remembered project rather than the one in the URL. */
    remembered: !routeKey && !!remembered,
    remember,
    forget,
  };
}
